import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Fingerprint, Loader2 } from "lucide-react";
import { auth, portal, preferTenant, tenantPreferred, trackSignupFunnel } from "./lib/api.js";
import * as passkey from "./lib/passkey.js";
import * as vault from "./lib/keyvault.js";
import { VaultUnlockDialog } from "./SecureImage.jsx";
import { C, fontStack, inputStyle } from "./theme.js";
import Admin from "./Admin.jsx";
import RentalApp from "./App.jsx";
import GuarantorPage from "./Guarantor.jsx";
import TenantApp from "./TenantApp.jsx";
import LegalPage, { LegalLinks } from "./Legal.jsx";

/**
 * שער הכניסה.
 *
 * בגרסת הארטיפקט "נעילת מכשיר" לדייר הייתה דגל ב-window.storage, שכל אחד
 * יכול היה למחוק מכלי הפיתוח. כאן המצב נקבע על ידי עוגיית סשן חתומה
 * שהשרת הנפיק, ולכן אין דרך להיכנס לצד הניהול מהאזור האישי.
 */
/* ניווט מינימלי. אין ראוטר בפרויקט, ואין צורך בו — יש בדיוק נתיב אחד
   מיוחד. השרת מגיש את ה-SPA לכל נתיב (catchall_document), ולכן /admin
   נטען כרגיל וגם רענון עליו עובד. */
const go = (path) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

export default function Root() {
  const [state, setState] = useState({ phase: "loading" });
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onNav = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onNav);
    return () => window.removeEventListener("popstate", onNav);
  }, []);

  const adminRoute = path.startsWith("/admin");

  /* /g/<token> — הערב. זהו הנתיב היחיד שאינו מנסה לזהות מי הגולש: הערב
     אינו משתמש במערכת, ובדיקת סשן כאן הייתה מכניסה אותו בטעות לאזור של
     דייר שנכנס מאותו מכשיר קודם. */
  const guarantorToken = path.startsWith("/g/")
    ? decodeURIComponent(path.slice(3)).replace(/\/+$/, "")
    : null;

  /* /t/<קוד> — קישור ישיר לאזור הדייר.
     בלעדיו הדייר נוחת על מסך פתיחת החשבון של בעל הנכס, צריך להבין שהוא
     לא אמור להירשם, למצוא כפתור בתחתית המסך ולהקליד שמונה ספרות. רובם
     פשוט נוטשים שם. הקוד ממילא מופיע באותה הודעת וואטסאפ, ולכן הכנסתו
     לקישור אינה חושפת דבר חדש — היא רק חוסכת את ההקלדה. */
  const tenantCode = path.startsWith("/t/")
    ? decodeURIComponent(path.slice(3)).replace(/\D/g, "")
    : null;
  /* ה-promise של הכניסה, ולא דגל בוליאני. ב-StrictMode (פיתוח) האפקט רץ
     פעמיים: עם דגל, ההרצה השנייה דילגה על הכניסה והריצה detect לפני
     שהעוגייה קיימת — והקישור /t/<קוד> נחת על מסך ההרשמה. עם promise,
     כל הרצה ממתינה לאותה כניסה אחת. הוא נשמר גם אחרי שהסתיים, כדי
     שיציאה מהאזור האישי לא תכניס את הדייר בחזרה. */
  const loginOnce = useRef(null);

  const detect = useCallback(async () => {
    /* מי שיש לו רק סשן דייר נכנס ישירות לאזור שלו — זה רוב הדיירים.
       כשיש בדפדפן גם סשן בעלים תקף, הבעלים גובר — אלא אם הכניסה לאזור
       הדייר בלשונית הזו הייתה מכוונת (קישור /t, קוד, "תצוגת הדייר").
       בלי ההכרעה הזו, דייר שהוא בעצמו בעל נכסים בפלטפורמה נפל אחרי
       קישור חוזה אחד על החוזה בכל כניסה — בלי דרך להגיע לנכסים שלו. */
    let tenant = null;
    try {
      tenant = await portal.me();
    } catch {
      /* לא דייר */
    }
    if (tenant && tenantPreferred()) return { phase: "tenant", ...tenant };
    try {
      const me = await auth.me();
      return { phase: "owner", me };
    } catch {
      /* לא בעלים */
    }
    if (tenant) return { phase: "tenant", ...tenant };
    return { phase: "gate" };
  }, []);

  useEffect(() => {
    if (guarantorToken) return undefined;
    let alive = true;

    (async () => {
      /* הקוד נצרך פעם אחת בלבד: detect רצה שוב גם ביציאה מהאזור האישי,
         ובלי השמירה הזו לחיצה על "יציאה" הייתה מכניסה את הדייר בחזרה. */
      if (tenantCode) {
        if (!loginOnce.current) {
          loginOnce.current = portal.login(tenantCode).finally(() => {
            /* מנקים את הקוד מהכתובת ומההיסטוריה. אחרי הכניסה הוא כבר יושב
               בעוגיית הסשן, ואין סיבה שיישאר בשורת הכתובת של הטלפון. */
            window.history.replaceState({}, "", "/");
          });
        }
        try {
          await loginOnce.current;
        } catch {
          // קוד שבוטל או הוחלף — נוחתים על מסך הזנת הקוד עם הסבר.
          if (alive) setState({ phase: "gate", mode: "tenant", badCode: true });
          return;
        }
      }

      const s = await detect();
      if (alive) setState(s);
    })();

    return () => {
      alive = false;
    };
  }, [detect, guarantorToken, tenantCode]);

  /* /privacy ו-/terms — עמודים ציבוריים. נפתחים בלשונית חדשה ממסכי
     הכניסה, ולכן לפני כל זיהוי: גם דייר וגם מי שאין לו חשבון קוראים אותם. */
  const legalRoute = path === "/privacy" ? "privacy" : path === "/terms" ? "terms" : null;
  if (legalRoute) return <LegalPage kind={legalRoute} onBack={() => go("/")} />;

  // לפני כל שאר הבדיקות, ובלי תלות במצב הזיהוי.
  if (guarantorToken) return <GuarantorPage token={guarantorToken} />;

  if (state.phase === "loading") return <Splash />;

  if (state.phase === "tenant") {
    return (
      <TenantApp
        initial={state}
        onExit={async () => {
          await portal.logout().catch(() => {});
          // בעל נכס שנכנס לאזור של הדייר עדיין מחזיק עוגיית ניהול תקפה,
          // ולכן הוא חוזר לניהול ולא למסך התחברות. detect בודק את שניהם.
          setState(await detect());
        }}
      />
    );
  }

  /* מסך הפלטפורמה יושב על נתיב ולא על לשונית: הוא לא חלק מניהול הנכסים,
     ורוב המשתמשים לעולם לא אמורים לראות שהוא קיים. השרת ממילא מחזיר 404
     לכל מי שאינו platform_admin, ולכן הבדיקה כאן היא נוחות ולא הגנה. */
  if (state.phase === "owner" && adminRoute) {
    if (state.me?.role !== "platform_admin") {
      return <NotFound onExit={() => go("/")} />;
    }
    return <Admin onExit={() => go("/")} />;
  }

  if (state.phase === "owner") {
    return (
      <>
        <RentalApp
          me={state.me}
          onSignOut={async () => {
            // נעילת הכספת ביציאה: המפתח הפרטי לא נשאר בזיכרון של לשונית
            // שמישהו אחר עשוי לפתוח אחרי.
            vault.lock();
            await auth.logout().catch(() => {});
            setState({ phase: "gate" });
          }}
        />
        {/* נשמע לאירוע vault:unlock מכל מקום באפליקציה. */}
        <VaultUnlockDialog />
      </>
    );
  }

  return (
    <Gate
      onDone={() => detect().then(setState)}
      initialMode={state.mode}
      badCode={state.badCode}
      onBack={state.back ? () => detect().then(setState) : null}
    />
  );
}

function NotFound({ onExit }) {
  return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center px-4"
      style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
      <div className="w-full max-w-sm rounded-2xl border p-6 text-center"
        style={{ background: C.card, borderColor: C.line }}>
        <h1 className="text-lg font-bold">הדף לא נמצא</h1>
        <button onClick={onExit} className="mt-4 w-full rounded-xl py-2.5 text-sm"
          style={{ background: C.teal, color: "#fff" }}>
          חזרה
        </button>
      </div>
    </div>
  );
}

function Splash() {
  return (
    <div
      dir="rtl"
      className="min-h-screen flex items-center justify-center"
      style={{ background: C.surface, color: C.muted, fontFamily: fontStack }}
    >
      <Loader2 className="animate-spin" size={22} />
    </div>
  );
}

/* ========================================================================== */

/**
 * שער הכניסה. ברירת המחדל היא הרשמה ולא התחברות: מי שמגיע לכתובת בלי
 * חשבון הוא הרוב, ומסך התחברות ריק לא אומר לו מה לעשות. מי שכבר רשום
 * מגיע כמעט תמיד עם עוגייה תקפה ולא רואה את המסך הזה בכלל.
 */
/* ברירת המחדל היא התחברות: קודם "כניסה", ומתחתיה "פתיחת חשבון" למי
   שעוד אין לו — מי שכבר רשום מגיע לכאן בכל יציאה, ומסך הרשמה בראש
   שלח אותו לחפש איפה נכנסים (25.8.2026). */
function Gate({ onDone, initialMode = "login", onBack = null, badCode = false }) {
  const [mode, setMode] = useState(initialMode);   // signup | login | code | tenant
  const [justRegistered, setJustRegistered] = useState("");

  return (
    <div
      dir="rtl"
      className="min-h-screen w-full flex items-center justify-center px-4 py-10"
      style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}
    >
      <div
        className="w-full max-w-sm rounded-2xl border p-6"
        style={{ background: C.card, borderColor: C.line }}
      >
        <div className="text-xs tracking-widest mb-1" style={{ color: C.ochre }}>
          ניהול נכסים
        </div>

        {mode === "signup" && (
          <OwnerSignup
            onLogin={(email) => { if (email) rememberEmail(email); setMode("login"); }}
            onRegistered={(email) => { setJustRegistered(email); setMode("login"); }}
          />
        )}
        {mode === "login" && (
          <OwnerLogin
            onDone={onDone}
            onSignup={() => { setJustRegistered(""); setMode("signup"); }}
            onForgot={() => setMode("code")}
            justRegistered={justRegistered}
          />
        )}
        {mode === "code" && <CodeLogin onDone={onDone} onBack={() => setMode("login")} />}
        {mode === "tenant" && <TenantLogin onDone={onDone} badCode={badCode} />}

        {mode !== "code" && (
        <button
          onClick={() => setMode(mode === "tenant" ? "signup" : "tenant")}
          className="mt-4 w-full rounded-xl py-2.5 text-sm"
          style={{ border: `1px solid ${C.line}`, color: C.muted }}
        >
          {mode === "tenant" ? "אני בעל נכס" : "כניסה לאזור הדייר"}
        </button>
        )}

        {/* מוצג רק כשהגענו לכאן מתוך חשבון מחובר, כדי שלא ייווצר מסך
            ללא מוצא לבעל נכס שפתח את שער הדייר בטעות. */}
        {onBack && (
          <button
            onClick={onBack}
            className="mt-2 w-full text-center text-[13px]"
            style={{ color: C.teal }}
          >
            חזרה לניהול
          </button>
        )}

        <LegalLinks className="mt-4" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- הרשמה --- */

const MIN_PASSWORD = 8;

/* האימייל האחרון שנכנס במכשיר הזה. נוחות בלבד — סיסמאות נשמרות במנהל
   הסיסמאות של הדפדפן ולא אצלנו, וטביעת אצבע נשמרת במכשיר עצמו. */
const LAST_EMAIL_KEY = "rentals:last-email";
const lastEmail = () => {
  try { return localStorage.getItem(LAST_EMAIL_KEY) || ""; } catch { return ""; }
};
const rememberEmail = (email) => {
  try { localStorage.setItem(LAST_EMAIL_KEY, email); } catch { /* מצב פרטי */ }
};

function OwnerSignup({ onRegistered, onLogin }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);

  const sessionId = useRef(crypto.randomUUID());
  const focused = useRef(new Set());
  useEffect(() => { trackSignupFunnel(sessionId.current, "view"); }, []);

  const trackFieldFocus = (field) => {
    if (focused.current.has(field)) return;
    focused.current.add(field);
    trackSignupFunnel(sessionId.current, "field_focus", { field });
  };

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErr(""); setEmailTaken(false); };

  /* הבדיקות רצות גם בשרת. כאן הן קיימות כדי שהמשתמש יידע מה חסר לפני
     שהוא שולח, ולא יקבל "חלק מהפרטים אינם תקינים" בלי לדעת איזה. */
  const problem =
    !f.name.trim() ? "צריך שם"
      : !/^\S+@\S+\.\S+$/.test(f.email) ? "כתובת אימייל לא תקינה"
        : f.password.length < MIN_PASSWORD ? `הסיסמה חייבת להיות באורך ${MIN_PASSWORD} תווים לפחות`
          : !agree ? "כדי לפתוח חשבון צריך לאשר את תנאי השימוש ומדיניות הפרטיות"
            : null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (problem) {
      setErr(problem);
      trackSignupFunnel(sessionId.current, "validation_error", {
        reason: f.password.length < MIN_PASSWORD ? "password_too_short" : "terms_not_accepted",
      });
      return;
    }

    setBusy(true);
    setErr("");
    setEmailTaken(false);
    const email = f.email.trim();
    trackSignupFunnel(sessionId.current, "submit_attempt", { email });
    try {
      // ההסכמה נרשמת בשרת (ביומן החשבון) עם גרסת המסמכים שאושרה.
      await auth.register(f.name.trim(), email, f.password, { acceptTerms: true });
      trackSignupFunnel(sessionId.current, "submit_result", { reason: "success" });
      /* ההרשמה לא מכניסה לאפליקציה. המשתמש עובר למסך הכניסה ומקליד את מה
         שבחר — זו ההזדמנות היחידה שבה הוא מוודא שהוא זוכר את הסיסמה בזמן
         שהיא עוד לפניו, וגם הרגע שבו הדפדפן מציע לשמור אותה. */
      rememberEmail(email);
      onRegistered(email);
    } catch (ex) {
      const taken = ex.status === 409;
      trackSignupFunnel(sessionId.current, "submit_result", {
        reason: taken ? "email_taken" : ex.status === 429 ? "rate_limited" : "server_error",
      });
      setEmailTaken(taken);
      setErr(ex.status === 429
        ? "יותר מדי ניסיונות הרשמה. נסו שוב בעוד שעה."
        : ex.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1 className="disp text-2xl font-bold mb-1">
        פתיחת חשבון
      </h1>
      <p className="text-sm mb-2" style={{ color: C.muted }}>
        נמאס לרדוף אחרי דיירים, לחפש חוזה ישן במגירה, ולנסות לזכור מתי
        הוקראו המונים בפעם האחרונה? אנחנו כאן בשביל זה — ניהול שכירות,
        גבייה, מונים וחוזים, במקום אחד ובלי בלגן.
      </p>
      <p className="text-sm mb-4" style={{ color: C.muted }}>
        ל-50 הנרשמים הראשונים: שנה שלמה בחינם לבעלי עד 2 נכסים, בלי כרטיס
        אשראי ובלי התחייבות — והנתונים שלכם תמיד מוצפנים ומנוהלים בהתאם
        לתיקון 13 לחוק הגנת הפרטיות.
      </p>

      <label className="text-[12px]" style={{ color: C.muted }}>שם מלא או שם העסק</label>
      <input
        value={f.name}
        onChange={set("name")}
        onFocus={() => trackFieldFocus("name")}
        autoComplete="name"
        placeholder="ישראל ישראלי"
        style={{ ...inputStyle, marginBottom: 10 }}
        required
      />

      <label className="text-[12px]" style={{ color: C.muted }}>אימייל</label>
      <input
        type="email"
        value={f.email}
        onChange={set("email")}
        onFocus={() => trackFieldFocus("email")}
        autoComplete="username"
        dir="ltr"
        style={{ ...inputStyle, marginBottom: 10 }}
        required
      />

      <label className="text-[12px]" style={{ color: C.muted }}>סיסמה</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={f.password}
          onChange={set("password")}
          onFocus={() => trackFieldFocus("password")}
          autoComplete="new-password"
          dir="ltr"
          style={inputStyle}
          required
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute top-1/2 left-2 text-[11px] px-1.5 py-1 rounded"
          style={{ transform: "translateY(-50%)", color: C.muted }}
        >
          {show ? "הסתרה" : "הצגה"}
        </button>
      </div>
      <div className="text-[11px] mt-1" style={{ color: f.password && f.password.length < MIN_PASSWORD ? C.ochre : C.muted }}>
        {MIN_PASSWORD} תווים לפחות
        {f.password ? ` · הוקלדו ${f.password.length}` : ""}
      </div>

      {/* הסכמה מפורשת לפני פתיחת החשבון — בעל הנכס הוא בעל המאגר של
          נתוני הדיירים שלו, והמסמכים מסבירים לו בדיוק מה זה אומר. */}
      <label className="mt-3 flex items-start gap-2 text-[12px] cursor-pointer" style={{ color: C.ink }}>
        <input type="checkbox" checked={agree}
          onChange={(e) => { setAgree(e.target.checked); setErr(""); if (e.target.checked) trackFieldFocus("agree"); }}
          className="mt-0.5" aria-label="אישור תנאי השימוש ומדיניות הפרטיות" />
        <span>
          קראתי ואני מסכים/ה ל
          <a href="/terms" target="_blank" rel="noreferrer" style={{ color: C.teal, textDecoration: "underline" }}>תנאי השימוש</a>
          {" "}ול
          <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: C.teal, textDecoration: "underline" }}>מדיניות הפרטיות</a>
        </span>
      </label>

      {err && <Notice text={err} />}

      {/* האימייל כבר רשום — הכי שכיח כישלון בפועל (לפי משפך ההרשמה). כפתור
          מיידי במקום להשאיר את המשתמש עם הודעת שגיאה בלי דרך המשך ברורה. */}
      {emailTaken && (
        <button
          type="button"
          onClick={() => onLogin(f.email.trim())}
          className="w-full rounded-xl py-2.5 text-sm mb-3"
          style={{ background: C.teal, color: "#fff" }}
        >
          מעבר להתחברות
        </button>
      )}

      <button
        type="submit"
        disabled={busy}
        className="mt-4 w-full rounded-xl py-3 text-sm"
        style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}
      >
        {busy ? "פותח חשבון…" : "פתיחת חשבון"}
      </button>

      <button
        type="button"
        onClick={() => onLogin(f.email.trim())}
        className="mt-3 w-full text-center text-[13px]"
        style={{ color: C.teal }}
      >
        כבר יש לי חשבון — כניסה
      </button>
    </form>
  );
}

function OwnerLogin({ onDone, onSignup, onForgot, justRegistered = "" }) {
  // האימייל האחרון נשמר במכשיר כדי שלא צריך להקליד אותו כל פעם. רק הוא —
  // סיסמה לא נשמרת כאן לעולם; לזה יש את מנהל הסיסמאות של הדפדפן.
  const [email, setEmail] = useState(() => justRegistered || lastEmail());
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [canPasskey, setCanPasskey] = useState(false);

  /* הכפתור מוצג רק כשבמכשיר הזה באמת הוגדרה כניסה מהירה — לא לכל מי
     שיש לו חיישן: כפתור שנכשל תמיד ("אין passkey") רק מבלבל. */
  useEffect(() => {
    if (!passkey.knownOnDevice()) return;
    passkey.hasDeviceSensor().then(setCanPasskey);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await auth.login(email, password);
      rememberEmail(email);
      await onDone();
    } catch (ex) {
      // ApiError כבר נושא הודעה מוכנה בעברית; 429 הוא המקרה היחיד שדורש
      // ניסוח משלנו כי השרת מחזיר אותו בלי גוף.
      setErr(ex.status === 429 ? "יותר מדי ניסיונות. נסו שוב בעוד כמה דקות." : ex.message);
      setBusy(false);
    }
  };

  const withPasskey = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const r = await passkey.login();
      if (r?.email) rememberEmail(r.email);
      // כמו בכניסה עם סיסמה — כניסת בעלים מבטלת העדפת דייר של הלשונית.
      preferTenant(false);
      await onDone();
    } catch (ex) {
      // ביטול על ידי המשתמש אינו שגיאה — פשוט חוזרים לטופס.
      if (!ex.cancelled) setErr(ex.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1 className="disp text-2xl font-bold mb-1">
        כניסה
      </h1>
      <p className="text-sm mb-4" style={{ color: C.muted }}>
        לניהול הנכסים, הגבייה והחוזים.
      </p>

      {justRegistered && (
        <div className="mb-4 rounded-lg p-2.5 text-sm flex items-start gap-2"
          style={{ background: "#EAF3EE", color: C.paid }}>
          <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
          <span>החשבון נפתח. היכנסו עם הסיסמה שבחרתם.</span>
        </div>
      )}

      {canPasskey && (
        <>
          <button
            type="button"
            onClick={withPasskey}
            disabled={busy}
            className="mb-3 w-full rounded-xl py-3 text-sm flex items-center justify-center gap-2"
            style={{ border: `1px solid ${C.line}`, color: C.teal, opacity: busy ? 0.7 : 1 }}
          >
            <Fingerprint size={16} /> כניסה בטביעת אצבע
          </button>
          <div className="flex items-center gap-2 mb-3" style={{ color: C.muted }}>
            <div style={{ height: 1, background: C.line, flex: 1 }} />
            <span className="text-[11px]">או עם סיסמה</span>
            <div style={{ height: 1, background: C.line, flex: 1 }} />
          </div>
        </>
      )}

      <label className="text-[12px]" style={{ color: C.muted }}>אימייל</label>
      <input
        type="email"
        autoComplete="username"
        dir="ltr"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ ...inputStyle, marginBottom: 10 }}
        required
      />

      <label className="text-[12px]" style={{ color: C.muted }}>סיסמה</label>
      <input
        type="password"
        autoComplete="current-password"
        dir="ltr"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        style={inputStyle}
        required
      />

      {err && <Notice text={err} />}

      <button
        type="submit"
        disabled={busy}
        className="mt-4 w-full rounded-xl py-3 text-sm"
        style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}
      >
        {busy ? "מתחבר…" : "כניסה"}
      </button>

      {onForgot && (
        <button
          type="button"
          onClick={onForgot}
          className="mt-3 w-full text-center text-[13px]"
          style={{ color: C.muted }}
        >
          שכחתי את הסיסמה
        </button>
      )}

      {onSignup && (
        <button
          type="button"
          onClick={onSignup}
          className="mt-2 w-full text-center text-[13px]"
          style={{ color: C.teal }}
        >
          אין לי חשבון — פתיחת חשבון
        </button>
      )}
    </form>
  );
}

/**
 * כניסה בקוד חד-פעמי — למי שאיבד את הסיסמה.
 *
 * לא "שולחים לך את הסיסמה במייל": הסיסמה שמורה כ-argon2 ואי אפשר לקרוא
 * אותה, וגם אילו היה אפשר — סיסמה חיה בתיבת דואר נשארת שם לנצח. במקום
 * זה קוד לשימוש אחד שמחזיר את הגישה, ואז קובעים סיסמה חדשה.
 *
 * שני שלבים במסך אחד: אימייל ← קוד. אחרי הכניסה מוצע לקבוע סיסמה, ומי
 * שיש לו הצפנת צילומי ת"ז מקבל אזהרה מפורשת שהיא נשארה נעולה.
 */
function CodeLogin({ onDone, onBack }) {
  const [step, setStep] = useState("email");   // email | code | password
  const [email, setEmail] = useState(() => lastEmail());
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [vaultLocked, setVaultLocked] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [minutes, setMinutes] = useState(15);

  const send = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErr("כתובת אימייל לא תקינה"); return; }
    setBusy(true); setErr("");
    try {
      const r = await auth.sendCode(email.trim());
      if (r?.minutes) setMinutes(r.minutes);
      rememberEmail(email.trim());
      setStep("code");
    } catch (ex) {
      setErr(ex.status === 429 ? "יותר מדי בקשות. נסו שוב בעוד כמה דקות." : ex.message);
    }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const r = await auth.verifyCode(email.trim(), code.replace(/\D/g, ""));
      // גם כניסה בקוד היא כניסת בעלים — מבטלת העדפת דייר של הלשונית.
      preferTenant(false);
      setVaultLocked(!!r?.vaultLocked);
      setStep("password");
    } catch (ex) {
      setErr(ex.message);
      setBusy(false);
      return;
    }
    setBusy(false);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await auth.resetPassword(password);
      await onDone();
    } catch (ex) {
      setErr(ex.message);
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="disp text-2xl font-bold mb-1">
        {step === "password" ? "סיסמה חדשה" : "כניסה בלי סיסמה"}
      </h1>

      {step === "email" && (
        <form onSubmit={send}>
          <p className="text-sm mb-4" style={{ color: C.muted }}>
            נשלח קוד בן 6 ספרות לאימייל שאיתו נפתח החשבון. הוא תקף {minutes} דקות.
          </p>
          <label className="text-[12px]" style={{ color: C.muted }}>אימייל</label>
          <input type="email" autoComplete="username" dir="ltr" value={email} autoFocus
            onChange={(e) => { setEmail(e.target.value); setErr(""); }}
            style={inputStyle} required />
          {err && <Notice text={err} />}
          <button type="submit" disabled={busy} className="mt-4 w-full rounded-xl py-3 text-sm"
            style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
            {busy ? "שולח…" : "שליחת קוד"}
          </button>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={verify}>
          <p className="text-sm mb-4" style={{ color: C.muted }}>
            שלחנו קוד ל-<span dir="ltr">{email}</span>. אם הוא לא הגיע — כדאי לבדוק בספאם.
          </p>
          <input value={code} inputMode="numeric" autoComplete="one-time-code" autoFocus
            onChange={(e) => { setCode(e.target.value); setErr(""); }}
            placeholder="000000" maxLength={6}
            style={{ ...inputStyle, textAlign: "center", fontSize: 26, letterSpacing: 8, padding: "12px 10px" }} />
          {err && <Notice text={err} />}
          <button type="submit" disabled={busy} className="mt-4 w-full rounded-xl py-3 text-sm"
            style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
            {busy ? "בודק…" : "כניסה"}
          </button>
          <button type="button" onClick={send} disabled={busy}
            className="mt-3 w-full text-center text-[13px]" style={{ color: C.muted }}>
            לא קיבלתי — שליחה מחדש
          </button>
        </form>
      )}

      {step === "password" && (
        <form onSubmit={savePassword}>
          <div className="mb-4 rounded-lg p-2.5 text-sm flex items-start gap-2"
            style={{ background: "#EAF3EE", color: C.paid }}>
            <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
            <span>נכנסת לחשבון. בחרו סיסמה חדשה כדי שלא תצטרכו קוד בפעם הבאה.</span>
          </div>

          {/* המפתח הפרטי של ההצפנה ארוז בסיסמה הישנה, ולשרת אין עותק —
              זה מה שמונע ממנו לפתוח את הצילומים, וזו גם הסיבה שכניסה
              בקוד לא יכולה לפתוח אותם. קוד השחזור הוא העותק השני. */}
          {vaultLocked && (
            <div className="mb-4 rounded-lg p-2.5 text-sm flex items-start gap-2"
              style={{ background: "#FBF2E3", color: "#6B4E12" }}>
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                צילומי תעודות הזהות נשארו מוצפנים בסיסמה הישנה. אחרי הכניסה אפשר
                לפתוח אותם עם <b>קוד השחזור</b> שקיבלתם בהפעלת ההצפנה. כל שאר
                הנתונים — חוזים, גבייה, קריאות — זמינים כרגיל.
              </span>
            </div>
          )}

          <label className="text-[12px]" style={{ color: C.muted }}>סיסמה חדשה</label>
          <input type="password" autoComplete="new-password" dir="ltr" value={password} autoFocus
            onChange={(e) => { setPassword(e.target.value); setErr(""); }}
            style={inputStyle} required />
          <div className="text-[11px] mt-1" style={{ color: password && password.length < MIN_PASSWORD ? C.ochre : C.muted }}>
            {MIN_PASSWORD} תווים לפחות
          </div>
          {err && <Notice text={err} />}
          <button type="submit" disabled={busy || password.length < MIN_PASSWORD}
            className="mt-4 w-full rounded-xl py-3 text-sm"
            style={{ background: C.teal, color: "#fff", opacity: busy || password.length < MIN_PASSWORD ? 0.7 : 1 }}>
            {busy ? "שומר…" : "שמירה וכניסה"}
          </button>
          <button type="button" onClick={onDone} className="mt-3 w-full text-center text-[13px]" style={{ color: C.muted }}>
            אחר כך — כניסה בלי לשנות סיסמה
          </button>
        </form>
      )}

      {step !== "password" && (
        <button type="button" onClick={onBack} className="mt-4 w-full rounded-xl py-2.5 text-sm"
          style={{ border: `1px solid ${C.line}`, color: C.muted }}>
          חזרה לכניסה עם סיסמה
        </button>
      )}
    </div>
  );
}

function TenantLogin({ onDone, badCode = false }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const clean = code.replace(/\D/g, "");
    if (clean.length < 6 || clean.length > 8) {
      setErr("הסיסמה בת 6–8 ספרות");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await portal.login(clean);
      await onDone();
    } catch (ex) {
      setErr(ex.status === 429 ? "יותר מדי ניסיונות. נסו שוב בעוד כמה דקות." : ex.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h1 className="disp text-2xl font-bold mb-1">
        אזור הדייר
      </h1>
      <p className="text-sm mb-4" style={{ color: C.muted }}>
        הזינו את הסיסמה שקיבלתם מבעל הנכס — בדרך כלל 6 הספרות האחרונות
        של תעודת הזהות שלכם.
      </p>

      {/* הגענו לכאן מקישור שהקוד שבו כבר לא תקף. בלי ההסבר הזה המסך
          נראה כאילו הקישור סתם לא עבד. */}
      {badCode && (
        <div className="mb-4 rounded-lg p-2.5 text-sm flex items-start gap-2"
          style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>הקישור שקיבלתם כבר אינו בתוקף. בקשו מבעל הנכס קוד חדש, או הזינו אותו כאן.</span>
        </div>
      )}

      <input
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
          setErr("");
        }}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        maxLength={8}
        style={{ ...inputStyle, textAlign: "center", fontSize: 26, letterSpacing: 8, padding: "12px 10px" }}
      />

      {err && <Notice text={err} />}

      <button
        type="submit"
        disabled={busy}
        className="mt-4 w-full rounded-xl py-3 text-sm"
        style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}
      >
        {busy ? "נכנס…" : "כניסה"}
      </button>
    </form>
  );
}

function Notice({ text }) {
  return (
    <div
      className="mt-3 rounded-lg p-2.5 text-sm flex items-start gap-2"
      style={{ background: "#FBF2E3", color: "#6B4E12" }}
    >
      <AlertTriangle size={15} className="mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}
